// ============================================================
// WhenEditor - the condition on one effect ("only when...").
//
// Clauses in a group all have to hold; any one group is enough. Each
// clause is a kind, a value (a list, free text for a tag or a name, or
// metres), and "not".
// With no condition there is one button, "Only when...".
//
// What can be asked is `CLAUSE_CHOICES` (`engine/character/effectWhen`):
// a new kind of clause appears here without an edit.
// ============================================================
import { useState } from 'react';
import { CLAUSE_CHOICES, blankClause, whenSentence } from '../../engine/character/effectWhen';
import { SELF_KINDS } from '../../engine/character/whenClauses';
import type { Clause, ClauseKind, When } from '../../engine/character/effectWhen';
import common from '../../styles/common.module.css';
import styles from '../../styles/Stances.module.css';

interface Props {
  when: When | undefined;
  /** Undefined: no condition - the effect simply applies. */
  onChange: (next: When | undefined) => void;
  /** What the condition is on, for the controls' accessible names. */
  label: string;
  locked: boolean;
  /**
   * A SHEET effect's condition (the two tiers, plan armoury): only clauses about oneself, since a sheet is worked
   * out before anybody is targeted or anything armed. The other kinds are shown greyed, with the reason.
   */
  selfOnly?: boolean;
}

/** A tag is typed, and taken when the field is left: an empty one is not a clause. */
function TagField({
  value,
  onCommit,
  label,
  locked,
}: {
  value: string;
  onCommit: (tag: string) => void;
  label: string;
  locked: boolean;
}) {
  const [draft, setDraft] = useState(value);
  return (
    <input
      className={`${common.input} ${styles.narrow}`}
      aria-label={label}
      value={draft}
      maxLength={40}
      disabled={locked}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => (draft.trim() ? onCommit(draft.trim()) : setDraft(value))}
    />
  );
}

/** The kinds whose value is a number, and the least each takes: metres above zero, armour from zero. */
const NUMERIC: Partial<Record<ClauseKind, number>> = { within: 1, armour_here: 0 };

/** A number, taken when the field is left: anything below its least is put back. */
function NumberField(props: { value: number; least: number } & Omit<FieldProps, 'value'>) {
  const { value, least, onCommit, label, locked } = props;
  const [draft, setDraft] = useState(String(value));
  const ok = draft.trim() !== '' && Number(draft) >= least;
  const commit = () => (ok ? onCommit(String(Number(draft))) : setDraft(String(value)));
  return (
    <input
      type="number"
      min={least}
      className={`${common.input} ${styles.narrow}`}
      aria-label={label}
      value={draft}
      disabled={locked}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
    />
  );
}

interface FieldProps {
  value: string;
  onCommit: (value: string) => void;
  label: string;
  locked: boolean;
}

/** The clause's value: a list when the kind offers one, metres for a distance, else free text. */
function ClauseValue({ clause, label, locked, onSet }: Omit<ClauseRowProps, 'onRemove'>) {
  const choice = CLAUSE_CHOICES.find((c) => c.kind === clause.kind);
  const field = { label: `${label}: value`, locked };
  if (choice?.options) {
    return (
      <select
        className={common.input}
        aria-label={field.label}
        value={String(clause.is)}
        disabled={locked}
        onChange={(e) => onSet({ ...clause, is: e.target.value } as Clause)}
      >
        {choice.options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }
  const least = NUMERIC[clause.kind];
  if (least !== undefined) {
    return (
      <NumberField
        key={clause.is}
        value={clause.is as number}
        least={least}
        onCommit={(n) => onSet({ ...clause, is: Number(n) } as Clause)}
        {...field}
      />
    );
  }
  return (
    <TagField
      key={String(clause.is)}
      value={String(clause.is)}
      onCommit={(is) => onSet({ ...clause, is } as Clause)}
      {...field}
    />
  );
}

/** The flag a kind carries beside its value: "or larger" for a size, "or closer" for a range band. */
const EXTRA_FLAG: Partial<Record<ClauseKind, { key: 'orLarger' | 'orCloser'; label: string }>> = {
  target_size: { key: 'orLarger', label: 'or larger' },
  range_band: { key: 'orCloser', label: 'or closer' },
};

interface ClauseRowProps {
  clause: Clause;
  label: string;
  locked: boolean;
  onSet: (next: Clause) => void;
  onRemove: () => void;
  selfOnly?: boolean;
}

/** May a clause of this kind go here? Any, unless the condition is a sheet effect's. */
const allowed = (kind: ClauseKind, selfOnly: boolean | undefined) => !selfOnly || SELF_KINDS.includes(kind);

function ClauseRow(props: ClauseRowProps) {
  const { clause, label, locked, onSet } = props;
  const extra = EXTRA_FLAG[clause.kind];
  return (
    <div className={styles.effect}>
      <select
        className={common.input}
        aria-label={`${label}: about`}
        value={clause.kind}
        disabled={locked}
        onChange={(e) => onSet(blankClause(e.target.value as ClauseKind))}
      >
        {CLAUSE_CHOICES.map((c) => (
          <option key={c.kind} value={c.kind} disabled={!allowed(c.kind, props.selfOnly)}>
            {allowed(c.kind, props.selfOnly)
              ? c.label
              : `${c.label} - not on the sheet: it is worked out before any attack`}
          </option>
        ))}
      </select>
      <ClauseValue clause={clause} label={label} locked={locked} onSet={onSet} />
      {extra && (
        <label className={common.label}>
          <input
            type="checkbox"
            checked={!!(clause as { [k: string]: unknown })[extra.key]}
            disabled={locked}
            onChange={(e) => onSet(flag(clause, extra.key, e.target.checked))}
          />{' '}
          {extra.label}
        </label>
      )}
      <label className={common.label}>
        <input
          type="checkbox"
          aria-label={`${label}: not`}
          checked={!!clause.not}
          disabled={locked}
          onChange={(e) => onSet(flag(clause, 'not', e.target.checked))}
        />{' '}
        not
      </label>
      <button
        type="button"
        className={common.btnSmall}
        aria-label={`Remove ${label}`}
        disabled={locked}
        onClick={props.onRemove}
      >
        {'✕'}
      </button>
    </div>
  );
}

/** The clause with a flag set, or the key gone: a flag is `true` or absent, never `false`, on the wire. */
function flag<C extends Clause>(clause: C, key: 'not' | 'orLarger' | 'orCloser', on: boolean): C {
  const { [key]: _was, ...rest } = clause as C & { not?: true; orLarger?: true; orCloser?: true };
  return (on ? { ...rest, [key]: true } : rest) as C;
}

/** `when` with one group replaced; an emptied group is dropped, and no groups is no condition. */
function withGroup(when: When, g: number, group: Clause[]): When | undefined {
  const next = when.map((was, i) => (i === g ? group : was)).filter((clauses) => clauses.length > 0);
  return next.length > 0 ? next : undefined;
}

/** Where a new clause starts: about the target and the attack, or - on a sheet effect - about oneself. */
const startOf = (selfOnly: boolean | undefined) =>
  selfOnly ? ({ first: 'own_state', next: 'own_state' } as const) : ({ first: 'target_size', next: 'attack' } as const);

/** One group of clauses that must all hold, with its "+ and". */
function ConditionGroup(props: Props & { when: When; group: Clause[]; g: number }) {
  const { when, group, g, label, locked, selfOnly, onChange } = props;
  const set = (next: Clause[]) => onChange(withGroup(when, g, next));
  return (
    <div>
      {g > 0 && <div className={styles.says}>or</div>}
      {group.map((clause, c) => (
        <ClauseRow
          key={c}
          selfOnly={selfOnly}
          clause={clause}
          label={`${label}, condition ${g + 1}.${c + 1}`}
          locked={locked}
          onSet={(next) => set(group.map((was, i) => (i === c ? next : was)))}
          onRemove={() => set(group.filter((_, i) => i !== c))}
        />
      ))}
      <button
        type="button"
        className={common.btnSmall}
        aria-label={`${label}: and, in condition ${g + 1}`}
        disabled={locked}
        onClick={() => set([...group, blankClause(startOf(selfOnly).next)])}
      >
        + and
      </button>
    </div>
  );
}

export default function WhenEditor(props: Props) {
  const { when, onChange, label, locked, selfOnly } = props;
  const start = startOf(selfOnly);
  if (!when) {
    return (
      <button
        type="button"
        className={common.btnSmall}
        aria-label={`${label}: only when`}
        disabled={locked}
        onClick={() => onChange([[blankClause(start.first)]])}
      >
        Only when…
      </button>
    );
  }
  return (
    <div role="group" aria-label={`${label}: condition`} title={whenSentence(when)}>
      {when.map((group, g) => (
        <ConditionGroup key={g} {...props} when={when} group={group} g={g} />
      ))}
      <button
        type="button"
        className={common.btnSmall}
        aria-label={`${label}: or`}
        disabled={locked}
        onClick={() => onChange([...when, [blankClause(start.next)]])}
      >
        + or
      </button>
    </div>
  );
}
